import { Component, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { forkJoin, Observable, Subscription } from "rxjs";

import { SurveyQuestionService } from "../core/services/survey-question.service";
import { SurveyAnswerService } from "../core/services/survey-answer.service";
import { InstitutionService } from "../core/services/institution.service";
import { LocalStorageUtil } from "../shared/utils/local-storage.util";

import {
  SpinnerService,
  ToasterService,
  MessageType,
  InquiryRequest,
  InquiryResponse,
  Institution,
  SurveyQuestion,
  SurveyAnswer,
  Visitor,
  ServiceConstant,
} from "../../../../shared-lib/src/public-api";
import { FormControl, Validators } from "@angular/forms";
import { EduFamilySurvey } from "projects/shared-lib/src/lib/model/edu-family-survey.model";
import { EncryptionUtil } from "../shared/utils/encryption-util";
import { EduFamilySurveyService } from "../core/services/edu-family-survey.service";
import { finalize } from "rxjs/operators";
import { MatDialog } from "@angular/material/dialog";
import { SearchResultsModalComponent } from "./seach-results-modal/seach-results-modal.component";
import { SurveyUserService } from "../core/services/survey-user.service";
import { SurveyUser } from "projects/shared-lib/src/lib/model/survey-user.model";

@Component({
  selector: "app-survey",
  templateUrl: "./survey.component.html",
  styleUrls: ["./survey.component.css"],
})
export class SurveyComponent implements OnInit {
  public errorMsg = "Something was wrong";
  public emailControl: FormControl;
  public needEmail = false;
  public districts: Institution[] = [];
  public surveys: EduFamilySurvey[] = [];
  public isEduFamily = false;
  public searchSurvey: SurveyUser = null;
  public instId = null;

  private subscriptions: Subscription[] = [];
  public userTypeName: string;

  constructor(
    public localStorageUtil: LocalStorageUtil,
    private toasterService: ToasterService,
    private encryptionUtil: EncryptionUtil,
    private activatedRoute: ActivatedRoute,
    private eduFamilyService: EduFamilySurveyService,
    private surveyUserService: SurveyUserService,
    private spinnerService: SpinnerService,
    private dialog: MatDialog,
    private router: Router
  ) {
    this.activatedRoute.params.subscribe((params) => {
      let cat: string = params['category'] ? params['category'] : 'edu-family';
      if (cat.toUpperCase() == 'edu-family'.toUpperCase()) {
        this.isEduFamily = true;
      }
      this.userTypeName = cat;
      this.instId = params['institutionId'] ? params['institutionId'] : null;
    });
  }

  private onError(error) {
    this.spinnerService.hide();
    this.toasterService.show(MessageType.SYSTEM_ERROR, error);
  }

  ngOnInit() {
    this.emailControl = new FormControl(null, [Validators.pattern('^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\\.[a-zA-Z]{2,5}$')]);

    if (this.localStorageUtil.getHouseHoldId(this.userTypeName)) {
      this.loadSurveys(this.localStorageUtil.getHouseHoldId(this.userTypeName));
    } else {
      this.needEmail = true;
    }
  }

  onSkip(event) {
    this.router.navigate(["/guidelines"]);
  }

  private searchByLastNameSuccess(response: InquiryResponse<EduFamilySurvey>) {
    if (response.operationSuccess) {
      const foundStudents = [...response.returnValues];
      if(foundStudents.length>0){
        this.showSearchResultsModal(foundStudents);
      }
      else{
        this.toasterService.show(MessageType.INFORMATIONAL, 'No students where found.')
      }
    } else if (response.messages.length > 0) {
      this.toasterService.show(
        response.messages[0].type,
        response.messages[0].text
      );
    } else {
      this.onError(ServiceConstant.somethingWentWrongMessage);
    }
  }

  public async searchByLastName({lastName, facilityId}) {
    const formattedLastName = lastName.trim().toUpperCase();
    const encryptedLastName = await this.encryptionUtil.encrypt(formattedLastName);
    this.spinnerService.show();
    this.subscriptions.push(
      this.eduFamilyService.searchByLastName(encryptedLastName, facilityId)
      .pipe(finalize(() => this.spinnerService.hide()))
      .subscribe(this.searchByLastNameSuccess, this.onError)
    );
  }

  private showSearchResultsModal(students: EduFamilySurvey[]){

    const dialogRef = this.dialog.open(SearchResultsModalComponent, {
      data: {
        surveys: students,
        districts: this.districts,
      },
      width: '33rem',
      autoFocus: false,
      maxHeight: '90vh',
    });

    dialogRef.afterClosed().subscribe(result => {
      if(result){
        this.searchByIdOrEmail(result);
      }
    });
  }

  public searchByIdOrEmail(searchField: string) {
    const formattedSearchField = searchField.trim().toUpperCase();
    this.loadSurveys(formattedSearchField);
  }

  public async searchByEmail(email: string) {
    console.log("loadSurveys")
    const encryptedEmail = await this.encryptionUtil.encrypt(email.trim().toUpperCase());
    console.log("Encryted email: " + encryptedEmail);
    this.spinnerService.show();
    this.surveyUserService.getSurveyByEmailUserTypeName(encryptedEmail,this.userTypeName)
    .pipe(finalize(() => this.spinnerService.hide()))
    .subscribe(res => {
      if (res.returnValues.length <= 0) {
        this.toasterService.show(
          MessageType.INFORMATIONAL,
          'Email does not exist.'
        );
        return;
      }
      const survey = {...res.first};
      this.localStorageUtil.saveSurveyId(this.userTypeName,email.trim().toUpperCase());
      this.localStorageUtil.saveSurveyUserId(this.userTypeName,survey.id.toString());
      this.searchSurvey = survey;
      this.toasterService.show(
        MessageType.INFORMATIONAL,
        'Email found.'
      );

    })
  }

  private async loadSurveys(param: string) {
    this.spinnerService.show();
    const isEmail = this.hasEmailFormat(param);
    if (isEmail) {
      const encryptedEmail = await this.encryptionUtil.encrypt(param);
      this.subscriptions.push(
        this.eduFamilyService
          .loadSurveyByEmail(encryptedEmail)
          .pipe(finalize(() => this.spinnerService.hide()))
          .subscribe(
            (res) => {
            if (res.returnValues.length <= 0) {
              this.toasterService.show(
                MessageType.INFORMATIONAL,
                'Email does not exist.'
              );
              return;
            }
            const firstStudent = {...res.first};
            this.localStorageUtil.saveHouseHoldId(this.userTypeName,firstStudent.houseHoldId);
            this.surveys =  res.returnValues;
            this.needEmail = false;
          },
          (err) => {
            this.needEmail = true;
            this.toasterService.show(
              MessageType.SYSTEM_ERROR,
              'Could not load data.'
            );
          }
        )
      );
    } else {
      this.subscriptions.push(
        this.eduFamilyService
          .loadSurveyByHouseholdId(param)
          .pipe(finalize(() => this.spinnerService.hide()))
          .subscribe(
            (res) => {
              if (res.returnValues.length <= 0) {
                this.toasterService.show(
                  MessageType.INFORMATIONAL,
                  'Household ID does not exist.'
                );
                return;
              }
              const firstStudent = {...res.first};
              this.localStorageUtil.saveHouseHoldId(this.userTypeName,firstStudent.houseHoldId);
              this.needEmail = firstStudent.email == null;
              this.surveys =  res.returnValues;
            },
            (err) => {
              this.toasterService.show(
                MessageType.SYSTEM_ERROR,
                'Could not load data.'
              );
            }
          )
      );
    }
  }

  

  private hasEmailFormat(email: string): boolean {
    const emailExp = /^[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~](\.?[-!#$%&'*+\/0-9=?A-Z^_a-z`{|}~])*@[a-zA-Z0-9](-*\.?[a-zA-Z0-9])*\.[a-zA-Z](-?[a-zA-Z0-9])+$/;

    if (!email)  {
      return false;
    }

    if (email.length > 256) {
      return false;
    }

    if (!emailExp.test(email)) {
      return false;
    }

    return true;
  }

  getSurveyId()
  {
    return this.localStorageUtil.getSurveyId(this.userTypeName);
  }
}
